import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  finalize,
  first,
  map,
  Observable,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { layerTypeLabelMap } from '../../../../constants/layer-type.constant';
import { LayerType } from '../../../../models/layer-type';
import { LoadingController } from '../../../../helpers/loading.controller';
import { ToastService } from '../../../../services/toast.service';
import { ReadLayersService } from '../../../../services/read-layers.service';
import { FileUtils } from '../../../../utils/file.utils';
import { TabItemLike } from '../../../tabs/tabs.component';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import CSVLayer from '@arcgis/core/layers/CSVLayer';
import Graphic from '@arcgis/core/Graphic';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Field from '@arcgis/core/layers/support/Field';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import * as rendererJsonUtils from '@arcgis/core/renderers/support/jsonUtils';
import {
  FileLayerItemResponse,
  FileLayerResponse,
} from 'app/shared/models/file-layer.model';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

export interface AddLayerEventLike {
  type: LayerType;
  url?: string;
  title?: string;
  layerResponse?: FileLayerResponse;
}

interface AddLayerFormLike {
  type: FormControl<LayerType>;
  url: FormControl<string>;
}

@Component({
  selector: 'app-add-layers-form',
  templateUrl: 'add-layers-form.component.html',
  styleUrls: ['add-layers-form.component.scss'],
})
export class AddLayersFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;
  @Input()
  groupLayer: __esri.GroupLayer;

  tabs: TabItemLike[] = [
    {
      text: this.translationService.getCurrentLanguage().url,
    },
    {
      text: this.translationService.getCurrentLanguage().file,
    },
  ];
  tabIndex = 0;

  formGroup: FormGroup<AddLayerFormLike>;

  layerTypeItems = [LayerType.WFS, LayerType.WMS].map((value) => {
    const label = layerTypeLabelMap.get(value);
    return { label, value };
  });

  allowedFilesExt = ['.zip', '.kml', '.kmz', '.csv'];
  dropZoneInfo = `${
    this.translationService.getCurrentLanguage()['supported-file-formats']
  }: .${this.allowedFilesExt.join(', ')}`;

  get isBtnAddDisabled() {
    return this.formGroup.invalid;
  }

  private _groupLayerListMap = new Map<LayerType, __esri.GroupLayer>();
  private _groupLayerSubscriptionMap = new Map<LayerType, Subscription>();

  private groupLayerChangesSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private readLayersService: ReadLayersService,
    private mainMapService: MainMapService,
    private toastService: ToastService,
    private loadingController: LoadingController,
    private screenService: ScreenService,
    public translationService: TranslationService
  ) {
    this.formGroup = this.formBuilder.group<AddLayerFormLike>({
      type: this.formBuilder.control<LayerType>(null, [Validators.required]),
      url: this.formBuilder.control<string>(null, [Validators.required]),
    });
  }

  ngOnInit() {
    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.formGroup.reset();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.groupLayer) {
      this.handleGroupLayer();
    }
  }

  ngOnDestroy() {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onBtnClearClickInternal(e) {
    this.removeAllLayers();
  }

  onSubmit(e) {
    e.preventDefault();
    if (!this.isBtnAddDisabled) {
      const values = this.formGroup.getRawValue();

      this.addLayers(values);

      this.formGroup.reset();
    }
  }

  onFileChange(file: File) {
    if (this.loadingController.isShown()) {
      return;
    }

    let observable: Observable<AddLayerEventLike>;
    if (FileUtils.isFileExt(file, '.zip')) {
      observable = this.readLayersService.readShapefile(file).pipe(
        map<FileLayerResponse, AddLayerEventLike>((res) => {
          return {
            type: LayerType.ShapeFile,
            layerResponse: res,
            title: file.name,
          };
        })
      );
    } else if (
      FileUtils.isFileExt(file, '.kml') ||
      FileUtils.isFileExt(file, '.kmz')
    ) {
      observable = this.readLayersService.readKmlFile(file).pipe(
        map<FileLayerResponse, AddLayerEventLike>((layers) => {
          return {
            type: LayerType.KML,
            layerResponse: layers,
            title: file.name,
          };
        })
      );
    } else {
      let auxObservable = this.readLayersService.generateLayerUrlFromFile(file);

      if (FileUtils.isFileExt(file, '.csv')) {
        observable = auxObservable.pipe(
          map((url) => {
            return {
              type: LayerType.CSV,
              url,
            };
          })
        );
      }
    }

    this.loadingController.show();
    observable
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (res) => {
          this.addLayers(res);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.translationService.getCurrentLanguage()['failed-upload-file']
          );
        },
      });
  }

  private handleGroupLayer() {
    this.groupLayerChangesSubscription?.unsubscribe();
    if (!this.groupLayer) {
      return;
    }

    this.groupLayerChangesSubscription = EsriUtils.on(
      this.groupLayer.layers,
      'change'
    )
      .pipe(takeUntil(this.lifecycle))
      .subscribe((changes: __esri.CollectionChangeEvent<__esri.Layer>) => {
        changes.removed?.forEach((layer) => {
          if (layer instanceof GroupLayer) {
            const subscription = this._groupLayerSubscriptionMap.get(
              layer.id as any
            );
            subscription?.unsubscribe();
          }
        });

        changes.added?.forEach((layer) => {
          if (layer instanceof GroupLayer) {
            this.trackGroupLayerType(layer);
          }
        });
      });

    this.groupLayer.layers.forEach((layer) => {
      if (layer instanceof GroupLayer) {
        this.trackGroupLayerType(layer);
      }
    });
  }

  private addLayers(event: AddLayerEventLike) {
    if (!event) {
      return;
    }

    let layer: __esri.Layer;
    switch (event.type) {
      case LayerType.WMS:
        layer = new WMSLayer({
          url: event.url,
        });
        break;
      case LayerType.WFS:
        layer = new WFSLayer({
          url: event.url,
        });
        break;
      case LayerType.CSV:
        layer = new CSVLayer({
          url: event.url,
        });
        break;
      case LayerType.KML:
      case LayerType.ShapeFile:
        layer = this.parseStruturalLayer(event);
        break;
    }

    if (layer) {
      this.loadingController.show();
      EsriUtils.loadAllLayers(layer)
        .pipe(
          finalize(() => {
            this.loadingController.hide();
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe({
          next: () => {
            this.addLayerBy(layer, event.type);

            this.toastService.success(
              this.translationService.getCurrentLanguage()[
                'layer-added-successfully'
              ]
            );

            this.groupLayer.visible = true;

            const extents = EsriUtils.flatLayers([layer])
              .map((x) => (x as any).fullExtent)
              .filter((x) => !!x);
            if (extents.length) {
              this.mainMapService.goTo(extents);
            }
          },
          error: (response) => {
            if (!response) {
              return;
            }

            this.toastService.error(
              this.translationService.getCurrentLanguage()['failed-load-layer']
            );
          },
        });
    }
  }

  private parseStruturalLayer(event: AddLayerEventLike) {
    let layer: __esri.Layer;

    type LayerHolder = {
      id: number;
      layer: __esri.FeatureLayer | __esri.GroupLayer;
    };
    const folderType = 'Folder';

    const layerTypeMap = new Map<string, Array<LayerHolder>>();
    const putLayerByType = (type: string, layerHolder: LayerHolder) => {
      let list = layerTypeMap.get(type);
      if (!list) {
        list = [];
        layerTypeMap.set(type, list);
      }

      list.push(layerHolder);
    };
    const getLayerByTypeAndId = (type: string, idOrFirst: number | boolean) => {
      const list = layerTypeMap.get(type);

      if (idOrFirst === true) {
        return list?.[0]?.layer;
      }

      return list?.find((x) => x.id === idOrFirst)?.layer;
    };

    const layersAddedSet = new Set<__esri.Layer>();

    let layers: __esri.Layer[] =
      event.layerResponse?.layers
        ?.map((item) => {
          const spatialReference = item.featureSet?.spatialReference;

          const graphics: Graphic[] = item.featureSet?.features?.map(
            (featureLike) => {
              if (
                featureLike.geometry &&
                !featureLike.geometry.spatialReference
              ) {
                featureLike.geometry.spatialReference = spatialReference as any;
              }

              const geometry = EsriUtils.geometryFromJson(featureLike.geometry);

              return new Graphic({
                attributes: featureLike.attributes,
                geometry,
              });
            }
          );

          if (!graphics?.length) {
            return null;
          }

          const geometryType =
            item.layerDefinition?.geometryType ?? graphics[0].geometry.type;

          const layer = new FeatureLayer({
            layerId: item.layerDefinition?.id ?? 0,
            title: item.layerDefinition?.name,
            objectIdField: item.layerDefinition?.objectIdField ?? '',
            fields:
              item.layerDefinition?.fields?.map((field) => {
                return Field.fromJSON(field);
              }) ?? [],
            source: graphics,
            maxScale: item.layerDefinition?.maxScale ?? 0,
            minScale: item.layerDefinition?.minScale ?? 0,
            geometryType: EsriUtils.normalizeGeomType(geometryType) as any,
          });

          if (item.layerDefinition?.drawingInfo?.renderer) {
            const renderer = rendererJsonUtils.fromJSON(
              item.layerDefinition.drawingInfo.renderer
            );
            (layer as __esri.FeatureLayer).renderer = renderer;
          }

          putLayerByType(geometryType, {
            id: layer.layerId,
            layer,
          });

          return layer;
        })
        ?.filter((x) => !!x) ?? [];

    const groupLayerHolderList: Array<LayerHolder> =
      event.layerResponse?.folders?.map((item) => {
        const layer = new GroupLayer({
          title: item.name,
        });

        const holder: LayerHolder = { id: item.id, layer };

        putLayerByType(folderType, holder);

        return holder;
      });

    if (groupLayerHolderList?.length) {
      const groupLayerList = event.layerResponse.folders
        .map((item) => {
          const groupLayer = getLayerByTypeAndId(
            folderType,
            item.id
          ) as __esri.GroupLayer;

          let foundAnyLayer = false;

          item.featureInfos?.forEach((x) => {
            const layer = getLayerByTypeAndId(x.type, x.id);
            if (layer) {
              foundAnyLayer = true;
              groupLayer.layers.add(layer);
              layersAddedSet.add(layer);
            }
          });

          if (!foundAnyLayer) {
            item.featureInfos?.forEach((x) => {
              if (x.type === folderType) {
                return;
              }

              const layer = getLayerByTypeAndId(x.type, true);
              if (layer && !layersAddedSet.has(layer)) {
                groupLayer.layers.add(layer);
                layersAddedSet.add(layer);
              }
            });
          }

          if (item.parentFolderId >= 0) {
            return null;
          }

          return groupLayer;
        })
        .filter((x) => !!x?.layers?.length);

      if (groupLayerList.length) {
        layers.forEach((layer) => {
          if (!layer.title && layer instanceof FeatureLayer) {
            layer.title = layer.geometryType;
          }
        });

        layers = groupLayerList;
      }
    }

    if (layers.length === 1) {
      layer = layers[0];
      if (!layer.title) {
        layer.title = event.title;
      }
    } else {
      layer = new GroupLayer({
        title: event.title,
        layers,
      });
    }

    return layer;
  }

  private addLayerBy(layer: __esri.Layer, layerType: LayerType) {
    let groupLayer: __esri.GroupLayer;
    if (this._groupLayerListMap.has(layerType)) {
      groupLayer = this._groupLayerListMap.get(layerType);
    } else {
      groupLayer = new GroupLayer({
        id: layerType,
      });

      this.trackGroupLayerType(groupLayer);
    }

    groupLayer.add(layer);
  }

  private trackGroupLayerType(groupLayer: __esri.GroupLayer) {
    const layerType = groupLayer.id as LayerType;
    if (
      this._groupLayerListMap.has(layerType) ||
      !Object.values(LayerType).includes(layerType)
    ) {
      return;
    }

    this._groupLayerListMap.set(layerType, groupLayer);

    const subscription = EsriUtils.on(groupLayer.layers, 'change')
      .pipe(
        first(() => {
          if (
            groupLayer.layers.length &&
            !this.groupLayer?.layers?.includes(groupLayer)
          ) {
            this.groupLayer?.add(groupLayer);
          } else if (!groupLayer.layers.length) {
            this._groupLayerListMap.delete(layerType);
            this._groupLayerSubscriptionMap.delete(layerType);

            groupLayer.removeAll();
            this.groupLayer?.remove(groupLayer);

            return true;
          }

          return false;
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();

    this._groupLayerSubscriptionMap.set(layerType, subscription);
  }

  private removeAllLayers() {
    const subscriptionList = Array.from(
      this._groupLayerSubscriptionMap.values()
    );
    for (const subscription of subscriptionList) {
      subscription.unsubscribe();
    }
  }
}
